<script>
	import { onMount } from 'svelte';
	import { fetchDiscoveryRuns, emailHypothesis } from '../lib/api.js';
	import Navbar from '../components/Navbar.svelte';
	import { Mail, ChevronDown, ChevronRight } from 'lucide-svelte';

	export let navigate;

	let runs = [];
	let loading = true;
	let emailedIds = new Set();
	let emailingId = null;
	let expandedRuns = new Set();

	$: latestRun = runs[0] || null;
	$: olderRuns = runs.slice(1);
	$: latestHypotheses = latestRun?.hypotheses || [];
	$: latestOpps = latestHypotheses.filter(h => h.category === 'opportunity').length;
	$: latestRisks = latestHypotheses.filter(h => h.category === 'risk').length;
	$: latestDocs = [...new Set(latestHypotheses.flatMap(h => h.context?.doc_references || []))].length;

	const TYPE_LABEL = {
		contradiction: 'Contradiction',
		cross_document_pattern: 'Pattern',
		temporal_staleness: 'Staleness',
		coverage_gap: 'Gap'
	};

	const SEV_ORDER = { critical: 0, high: 1, medium: 2, low: 3 };

	function sortBySev(hs) {
		return [...hs].sort((a, b) => (SEV_ORDER[a.severity] ?? 4) - (SEV_ORDER[b.severity] ?? 4));
	}
	function getOpps(hs) { return sortBySev(hs.filter(h => h.category === 'opportunity')); }
	function getRisks(hs) { return sortBySev(hs.filter(h => h.category === 'risk')); }

	function toggleRun(runId) {
		if (expandedRuns.has(runId)) expandedRuns.delete(runId);
		else expandedRuns.add(runId);
		expandedRuns = expandedRuns;
	}

	async function handleEmail(id, e) {
		e?.stopPropagation();
		if (emailingId || emailedIds.has(id)) return;
		emailingId = id;
		try {
			await emailHypothesis(id);
			emailedIds = new Set([...emailedIds, id]);
		} catch (err) {
			console.error('Failed to email hypothesis:', err);
		}
		emailingId = null;
	}

	function humanizeReasoning(text, ctx) {
		if (!text || !ctx?.doc_references || !ctx?.doc_titles) return text;
		let result = text;
		ctx.doc_references.forEach((ref, i) => {
			const title = ctx.doc_titles[i];
			if (ref && title) {
				result = result.replaceAll("'" + ref + "'", "'" + title + "'");
				result = result.replaceAll(ref, title);
			}
		});
		return result;
	}

	function allSources(h) {
		const sources = [];
		const refs = h.context?.doc_references || [];
		const titles = h.context?.doc_titles || [];
		for (let i = 0; i < refs.length; i++) {
			sources.push({ type: 'internal', label: titles[i] || refs[i], ref: refs[i] });
		}
		if (h.discovery?.sources) {
			for (const s of h.discovery.sources) {
				if (s.type === 'external') sources.push(s);
			}
		}
		return sources;
	}

	function getAction(h) { return h.discovery?.action || h.recommended_action || null; }
	function getConf(h) { return h.discovery?.confidence ?? null; }

	function splitLead(text) {
		if (!text) return { lead: '', rest: '' };
		const m = text.match(/^(.+?[.!?])\s+(.+)$/s);
		if (m && m[1].length >= 60) return { lead: m[1], rest: m[2] };
		return { lead: text, rest: '' };
	}

	function relDate(iso) {
		if (!iso) return '';
		const ms = Date.now() - new Date(iso).getTime();
		const m = Math.floor(ms / 60000);
		if (m < 2) return 'just now';
		if (m < 60) return m + 'm ago';
		const hrs = Math.floor(m / 60);
		if (hrs < 24) return hrs + 'h ago';
		const days = Math.floor(hrs / 24);
		if (days < 7) return days + 'd ago';
		return absDate(iso);
	}

	function absDate(iso) {
		if (!iso) return '';
		return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
	}

	async function load() {
		loading = true;
		const data = await fetchDiscoveryRuns({ runLimit: 10 });
		runs = data.runs || [];
		loading = false;
	}

	onMount(load);
</script>

<svelte:head><title>Discover - ADRASI</title></svelte:head>

<div class="page">
	<Navbar {navigate} active="discover" on:refresh={load} />

	<div class="hero">
		<div class="hero-inner">
			{#if loading}
				<p class="hero-head hero-loading">Analyzing your intelligence&hellip;</p>
			{:else if latestRun}
				<p class="hero-head">
					<span class="hero-n hero-n-opp">{latestOpps}</span>
					{latestOpps === 1 ? 'opportunity' : 'opportunities'}&ensp;&middot;&ensp;<span class="hero-n hero-n-risk">{latestRisks}</span>
					{latestRisks === 1 ? 'risk' : 'risks'}
				</p>
				<p class="hero-sub">
					{#if latestDocs > 0}Across {latestDocs} {latestDocs === 1 ? 'document' : 'documents'}&ensp;&middot;&ensp;{/if}{relDate(latestRun.run_created_at)}{#if latestRun.fact_count}&ensp;&middot;&ensp;{latestRun.fact_count} facts analyzed{/if}
				</p>
			{:else}
				<p class="hero-head">No discoveries yet</p>
				<p class="hero-sub">Run a discovery cycle to surface opportunities and risks across your documents.</p>
			{/if}
		</div>
	</div>

	<div class="discover">
		{#if loading}
			<div class="disc-state disc-loading">Loading&hellip;</div>
		{:else if runs.length === 0}
			<div class="disc-state">No discovery cycles have run yet.</div>
		{:else}

			<div class="run-meta">
				<span class="run-label">Latest</span>
				<span class="run-date">{absDate(latestRun.run_created_at)}</span>
				<span class="run-count">{latestHypotheses.length} {latestHypotheses.length === 1 ? 'discovery' : 'discoveries'}</span>
			</div>

			{#if latestHypotheses.length === 0}
				<div class="disc-state">No discoveries in this cycle.</div>
			{:else}
				{@const opps = getOpps(latestHypotheses)}
				{@const risks = getRisks(latestHypotheses)}
				<div class="table-wrap">
					<table>
						<thead>
							<tr>
								<th style="width:20%">Finding</th>
								<th style="width:36%">Signal</th>
								<th style="width:24%">Action</th>
								<th style="width:20%">Sources</th>
							</tr>
						</thead>
						<tbody>
							{#if opps.length > 0}
								<tr class="group-hdr"><td colspan="4">Opportunities &nbsp;&middot;&nbsp; {opps.length}</td></tr>
								{#each opps as h (h.id)}
									{@const sources = allSources(h)}
									{@const conf = getConf(h)}
									{@const sig = splitLead(humanizeReasoning(h.reasoning, h.context))}
									<tr class="disc-row disc-sev-{h.severity}" class:row-resolved={h.status === 'resolved'}>
										<td class="td-finding">
											<div class="finding-title">{h.title}</div>
											<div class="finding-meta">
												<span class="fm-sev fm-sev-{h.severity}">{h.severity}</span>
												<span class="fm-type">{TYPE_LABEL[h.hypothesis_type] || h.hypothesis_type}</span>
											</div>
											{#if conf !== null}<div class="finding-bottom"><span class="fm-conf">{Math.round(conf * 100)}% confidence</span></div>{/if}
											<button class="row-email" class:row-email-sent={emailedIds.has(h.id)} disabled={emailingId === h.id || emailedIds.has(h.id)} on:click={(e) => handleEmail(h.id, e)} title="email me this discovery">
												{#if emailedIds.has(h.id)}sent{:else if emailingId === h.id}sending&hellip;{:else}email me{/if}
												<Mail size={11} strokeWidth={1.5} />
											</button>
										</td>
										<td class="td-signal">{#if sig.rest}<span class="sig-lead">{sig.lead}</span> {sig.rest}{:else}{sig.lead}{/if}</td>
										<td class="td-action td-action-opp">{getAction(h) || ''}</td>
										<td class="td-sources">
											<div class="sources-list">
												{#each sources as s}
													{#if s.type === 'internal' && s.ref}
														<a class="src-link" href="/zen?ref={encodeURIComponent(s.ref)}" target="_blank">{s.label}</a>
													{:else if s.type === 'external' && s.url}
														<a class="src-link src-ext" href={s.url} target="_blank" rel="noopener">{s.label}</a>
													{:else}
														<span class="src-plain">{s.label}</span>
													{/if}
												{/each}
											</div>
										</td>
									</tr>
								{/each}
							{/if}

							{#if risks.length > 0}
								<tr class="group-hdr"><td colspan="4">Risks &nbsp;&middot;&nbsp; {risks.length}</td></tr>
								{#each risks as h (h.id)}
									{@const sources = allSources(h)}
									{@const conf = getConf(h)}
									{@const sig = splitLead(humanizeReasoning(h.reasoning, h.context))}
									<tr class="disc-row disc-sev-{h.severity}" class:row-resolved={h.status === 'resolved'}>
										<td class="td-finding">
											<div class="finding-title">{h.title}</div>
											<div class="finding-meta">
												<span class="fm-sev fm-sev-{h.severity}">{h.severity}</span>
												<span class="fm-type">{TYPE_LABEL[h.hypothesis_type] || h.hypothesis_type}</span>
											</div>
											{#if conf !== null}<div class="finding-bottom"><span class="fm-conf">{Math.round(conf * 100)}% confidence</span></div>{/if}
											<button class="row-email" class:row-email-sent={emailedIds.has(h.id)} disabled={emailingId === h.id || emailedIds.has(h.id)} on:click={(e) => handleEmail(h.id, e)} title="email me this discovery">
												{#if emailedIds.has(h.id)}sent{:else if emailingId === h.id}sending&hellip;{:else}email me{/if}
												<Mail size={11} strokeWidth={1.5} />
											</button>
										</td>
										<td class="td-signal">{#if sig.rest}<span class="sig-lead">{sig.lead}</span> {sig.rest}{:else}{sig.lead}{/if}</td>
										<td class="td-action td-action-risk">{getAction(h) || ''}</td>
										<td class="td-sources">
											<div class="sources-list">
												{#each sources as s}
													{#if s.type === 'internal' && s.ref}
														<a class="src-link" href="/zen?ref={encodeURIComponent(s.ref)}" target="_blank">{s.label}</a>
													{:else if s.type === 'external' && s.url}
														<a class="src-link src-ext" href={s.url} target="_blank" rel="noopener">{s.label}</a>
													{:else}
														<span class="src-plain">{s.label}</span>
													{/if}
												{/each}
											</div>
										</td>
									</tr>
								{/each}
							{/if}
						</tbody>
					</table>
				</div>
			{/if}

			{#each olderRuns as run (run.run_id)}
				{@const runOpps = getOpps(run.hypotheses)}
				{@const runRisks = getRisks(run.hypotheses)}
				<div class="older-run">
					<!-- svelte-ignore a11y-click-events-have-key-events -->
					<div class="older-hdr" on:click={() => toggleRun(run.run_id)}>
						<span class="older-chevron">
							{#if expandedRuns.has(run.run_id)}<ChevronDown size={13} strokeWidth={1.5} />{:else}<ChevronRight size={13} strokeWidth={1.5} />{/if}
						</span>
						<span class="older-date">{absDate(run.run_created_at)}</span>
						<span class="older-rel">{relDate(run.run_created_at)}</span>
						<span class="older-count">{run.hypotheses.length} {run.hypotheses.length === 1 ? 'discovery' : 'discoveries'}</span>
					</div>

					{#if expandedRuns.has(run.run_id)}
						<div class="table-wrap table-wrap-older">
							<table>
								<thead>
									<tr>
										<th style="width:20%">Finding</th>
										<th style="width:36%">Signal</th>
										<th style="width:24%">Action</th>
										<th style="width:20%">Sources</th>
									</tr>
								</thead>
								<tbody>
									{#if runOpps.length > 0}
										<tr class="group-hdr"><td colspan="4">Opportunities &nbsp;&middot;&nbsp; {runOpps.length}</td></tr>
										{#each runOpps as h (h.id)}
											{@const sources = allSources(h)}
											{@const conf = getConf(h)}
											<tr class="disc-row disc-sev-{h.severity}" class:row-resolved={h.status === 'resolved'}>
												<td class="td-finding">
													<div class="finding-title">{h.title}</div>
													<div class="finding-meta">
														<span class="fm-sev fm-sev-{h.severity}">{h.severity}</span>
														<span class="fm-type">{TYPE_LABEL[h.hypothesis_type] || h.hypothesis_type}</span>
													</div>
													{#if conf !== null}<div class="finding-bottom"><span class="fm-conf">{Math.round(conf * 100)}% confidence</span></div>{/if}
												</td>
												<td class="td-signal">{humanizeReasoning(h.reasoning, h.context) || ''}</td>
												<td class="td-action td-action-opp">{getAction(h) || ''}</td>
												<td class="td-sources">
													<div class="sources-list">
														{#each sources as s}
															{#if s.type === 'internal' && s.ref}
																<a class="src-link" href="/zen?ref={encodeURIComponent(s.ref)}" target="_blank">{s.label}</a>
															{:else if s.type === 'external' && s.url}
																<a class="src-link src-ext" href={s.url} target="_blank" rel="noopener">{s.label}</a>
															{:else}
																<span class="src-plain">{s.label}</span>
															{/if}
														{/each}
													</div>
												</td>
											</tr>
										{/each}
									{/if}
									{#if runRisks.length > 0}
										<tr class="group-hdr"><td colspan="4">Risks &nbsp;&middot;&nbsp; {runRisks.length}</td></tr>
										{#each runRisks as h (h.id)}
											{@const sources = allSources(h)}
											{@const conf = getConf(h)}
											<tr class="disc-row disc-sev-{h.severity}" class:row-resolved={h.status === 'resolved'}>
												<td class="td-finding">
													<div class="finding-title">{h.title}</div>
													<div class="finding-meta">
														<span class="fm-sev fm-sev-{h.severity}">{h.severity}</span>
														<span class="fm-type">{TYPE_LABEL[h.hypothesis_type] || h.hypothesis_type}</span>
													</div>
													{#if conf !== null}<div class="finding-bottom"><span class="fm-conf">{Math.round(conf * 100)}% confidence</span></div>{/if}
												</td>
												<td class="td-signal">{humanizeReasoning(h.reasoning, h.context) || ''}</td>
												<td class="td-action td-action-risk">{getAction(h) || ''}</td>
												<td class="td-sources">
													<div class="sources-list">
														{#each sources as s}
															{#if s.type === 'internal' && s.ref}
																<a class="src-link" href="/zen?ref={encodeURIComponent(s.ref)}" target="_blank">{s.label}</a>
															{:else if s.type === 'external' && s.url}
																<a class="src-link src-ext" href={s.url} target="_blank" rel="noopener">{s.label}</a>
															{:else}
																<span class="src-plain">{s.label}</span>
															{/if}
														{/each}
													</div>
												</td>
											</tr>
										{/each}
									{/if}
								</tbody>
							</table>
						</div>
					{/if}
				</div>
			{/each}

		{/if}
	</div>
</div>

<style>
	* { box-sizing: border-box; }

	.page {
		min-height: 100vh;
		background: #111827;
		display: flex;
		flex-direction: column;
		font-family: 'Segoe UI', system-ui, sans-serif;
	}

	/* ================================================================
	   HERO
	   ================================================================ */
	.hero {
		background: #111827;
		padding: 48px 24px 40px;
		flex-shrink: 0;
		border-bottom: 1px solid #1F2937;
	}
	.hero-inner { max-width: 1200px; margin: 0 auto; }
	.hero-head {
		font-size: 28px;
		font-weight: 300;
		color: #F9FAFB;
		letter-spacing: -0.5px;
		line-height: 1.3;
		margin: 0;
	}
	.hero-n { font-weight: 600; }
	.hero-n-opp { color: #E5E7EB; }
	.hero-n-risk { color: #E5E7EB; }
	.hero-sub { font-size: 13px; color: #4B5563; margin: 10px 0 0; letter-spacing: 0.01em; }
	.hero-loading { color: #4B5563; animation: pulse 1.8s ease-in-out infinite; }
	@keyframes pulse { 0%, 100% { opacity: 0.4; } 50% { opacity: 1; } }

	/* ================================================================
	   CONTENT
	   ================================================================ */
	.discover {
		max-width: 1200px;
		margin: 0 auto;
		width: 100%;
		padding: 28px 24px 80px;
	}

	.disc-state {
		text-align: center;
		padding: 64px 20px;
		font-size: 14px;
		color: #4B5563;
	}
	.disc-loading { animation: pulse 1.8s ease-in-out infinite; }

	.run-meta { display: flex; align-items: baseline; gap: 10px; margin-bottom: 16px; }
	.run-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: #4B5563; }
	.run-date { font-size: 13px; font-weight: 500; color: #6B7280; }
	.run-count { font-size: 12px; color: #4B5563; margin-left: auto; }

	/* ================================================================
	   TABLE
	   ================================================================ */
	.table-wrap {
		border-radius: 6px;
		border: 1px solid #374151;
		overflow: hidden;
		margin-bottom: 40px;
	}

	table { width: 100%; border-collapse: collapse; table-layout: fixed; font-size: 13px; }

	thead tr { background: #1F2937; }
	thead th {
		padding: 9px 14px;
		text-align: left;
		font-size: 0.68rem;
		font-weight: 700;
		text-transform: uppercase;
		letter-spacing: 0.08em;
		color: #6B7280;
		border-bottom: 1px solid #374151;
		white-space: nowrap;
	}

	tbody tr { border-bottom: 1px solid #1F2937; transition: background 120ms; }
	tbody tr:last-child { border-bottom: none; }

	.group-hdr td {
		background: #0F172A;
		color: #4B5563;
		font-size: 0.67rem;
		font-weight: 700;
		text-transform: uppercase;
		letter-spacing: 0.1em;
		padding: 6px 14px;
		border-top: 1px solid #374151;
		border-bottom: 1px solid #1F2937;
	}

	/* ================================================================
	   DISC ROW
	   ================================================================ */
	.disc-row { vertical-align: top; }
	.disc-row:hover { background: rgba(255,255,255,0.025); }
	.disc-sev-critical > .td-finding { border-left: 2px solid #6B7280; }
	.disc-sev-high > .td-finding     { border-left: 1px solid #4B5563; }
	.row-resolved { opacity: 0.4; }

	/* FINDING */
	.td-finding {
		padding: 14px 14px 42px;
		position: relative;
		word-break: break-word;
		display: flex;
		flex-direction: column;
		gap: 0;
	}
	.finding-title {
		font-size: 0.83rem;
		font-weight: 600;
		color: #E2E8F0;
		line-height: 1.45;
		margin-bottom: 8px;
	}
	.finding-meta { display: flex; flex-direction: column; align-items: flex-start; gap: 0; }
	.finding-bottom {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-top: auto;
		padding-top: 12px;
		gap: 8px;
	}

	.fm-type { font-size: 0.63rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: #6B7280; display: block; margin-top: 4px; }
	.fm-sev {
		font-size: 0.63rem; font-weight: 700; text-transform: uppercase;
		letter-spacing: 0.05em; padding: 1px 5px; border-radius: 2px;
	}
	.fm-sev-critical { background: #111827; color: #E5E7EB; }
	.fm-sev-high     { background: #374151; color: #E5E7EB; }
	.fm-sev-medium   { background: #6B7280; color: #F3F4F6; }
	.fm-sev-low      { background: #D1D5DB; color: #374151; }
	.fm-conf { font-size: 0.65rem; color: #374151; letter-spacing: 0.01em; white-space: nowrap; }

	.row-email {
		position: absolute;
		bottom: 12px;
		left: 14px;
		opacity: 0;
		display: flex;
		align-items: center;
		gap: 5px;
		background: none;
		border: 1px solid #374151;
		border-radius: 3px;
		color: #6B7280;
		cursor: pointer;
		padding: 3px 8px;
		font-size: 0.68rem;
		font-family: inherit;
		white-space: nowrap;
		transition: opacity 120ms, color 120ms, border-color 120ms;
	}
	.disc-row:hover .row-email { opacity: 1; }
	.row-email:hover { color: #9CA3AF; border-color: #6B7280; }
	.row-email:disabled { opacity: 0.35 !important; cursor: default; }
	.row-email-sent { color: #34D399 !important; border-color: #065F46 !important; opacity: 1 !important; }

	/* SIGNAL */
	.td-signal {
		padding: 14px 14px;
		font-size: 0.78rem;
		color: #9CA3AF;
		line-height: 1.68;
		word-break: break-word;
	}
	.sig-lead { font-weight: 600; color: #CBD5E1; }

	/* ACTION */
	.td-action { padding: 14px 14px; font-size: 0.78rem; line-height: 1.68; word-break: break-word; }
	.td-action-opp { color: #6495ED; }
	.td-action-risk { color: #6495ED; }

	/* SOURCES */
	.td-sources { padding: 14px 14px; word-break: break-word; }
	.sources-list { display: flex; flex-direction: column; gap: 6px; }
	.src-link {
		font-size: 0.75rem;
		color: #6B7280;
		text-decoration: none;
		line-height: 1.4;
		display: block;
		transition: color 120ms;
	}
	.src-link:hover { color: #9CA3AF; text-decoration: underline; text-underline-offset: 2px; }
	.src-ext { font-style: italic; }
	.src-plain { font-size: 0.75rem; color: #4B5563; line-height: 1.4; display: block; }

	/* ================================================================
	   OLDER RUNS
	   ================================================================ */
	.older-run { border-top: 1px solid #1F2937; }
	.older-run:last-child { border-bottom: 1px solid #1F2937; }

	.older-hdr { display: flex; align-items: center; gap: 10px; padding: 12px 2px; cursor: pointer; user-select: none; }
	.older-hdr:hover .older-date { color: #9CA3AF; }
	.older-chevron { color: #4B5563; display: flex; align-items: center; flex-shrink: 0; }
	.older-date { font-size: 13px; font-weight: 500; color: #6B7280; transition: color 120ms; }
	.older-rel { font-size: 12px; color: #374151; }
	.older-count { font-size: 12px; color: #374151; margin-left: auto; }

	.table-wrap-older { margin-bottom: 16px; border-radius: 4px; margin-top: 10px; }

	/* ================================================================
	   RESPONSIVE
	   ================================================================ */
	@media (max-width: 768px) {
		.hero { padding: 32px 20px 28px; }
		.hero-head { font-size: 22px; }
		.discover { padding: 20px 16px 60px; }
		.td-action, .td-sources { display: none; }
	}
</style>
